import React, { useEffect, useState } from "react";
import "./createPlan.css";
import {
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
} from "@mui/material";
import { ReactComponent as PlusIcon } from "../../../assets/icons/plus.svg";
import { ReactComponent as DropdownIcon } from "../../../assets/icons/arrow-down.svg";
import {
  addPlanAsync,
  editPlanAsync,
  fetchPlanDetailsAsync,
} from "../../../redux/reducers/plansSlice";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Toast from "../../../components/toast/toast";
import { adminTexts } from "../../../utils/constants";
import { ReactComponent as BackBtn } from "../../../assets/icons/arrow-down.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/icons/trash.svg";

const CreatePlan = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();
  const { planDetails, isPlanAdded, isError } = useSelector((state) => ({
    planDetails: state?.plansData?.selectedPlan,
    isPlanAdded: state?.plansData?.isAddingPlan,
    isError: state?.plansData?.isError,
  }));

  const [planData, setPlanData] = useState({
    name: "",
    description: "",
    sku: "",
    activation_charge: "",
    device_type: "",
    plan_rates: [],
  });

  const [rateData, setRateData] = useState({
    min_line: "",
    max_line: "",
    price: "",
  });

  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchPlanDetailsAsync(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (planDetails && id) {
      setPlanData({
        name: planDetails.name || "",
        description: planDetails.description || "",
        sku: planDetails.sku || "",
        activation_charge: planDetails.activation_charge || "",
        device_type: planDetails.device_type || "",
        plan_rates: planDetails.planRate
          ? planDetails.planRate.map((rate) => ({
              id: rate.id,
              min_line: rate.min_line.toString(),
              max_line: rate.max_line.toString(),
              price: rate.price.toString(),
            }))
          : [],
      });
    }
  }, [planDetails, id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "activation_charge") {
      if (/^\d{0,7}(\.\d{0,2})?$/.test(value) || value === "") {
        setPlanData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    } else {
      setPlanData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleRateChange = (e) => {
    const { name, value } = e.target;

    if (/^\d*\.?\d{0,2}$/.test(value) || value === "") {
      setRateData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleEditRateChange = (index, field, value) => {
    if (/^\d*\.?\d{0,2}$/.test(value) || value === "") {
      setPlanData((prev) => {
        const updatedRates = [...prev.plan_rates];
        updatedRates[index] = { ...updatedRates[index], [field]: value };
        return { ...prev, plan_rates: updatedRates };
      });
    }
  };

  const handleDeleteRate = (index) => {
    setPlanData((prev) => ({
      ...prev,
      plan_rates: prev.plan_rates.filter((_, i) => i !== index),
    }));
  };

  const validateForm = () => {
    let isValid = true;
    let validationErrors = {};

    // Validate general fields
    Object.keys(planData)?.forEach((field) => {
      if (
        !planData[field] &&
        field !== "plan_rates" &&
        field !== "device_type" &&
        field !== "description"
      ) {
        validationErrors[field] = "This field is required";
        isValid = false;
      }
    });

    // If there's no ID (i.e., creating a new plan), check if plan_rates have all required fields
    if (!id) {
      planData.plan_rates.forEach((rate, index) => {
        if (!rate.min_line || !rate.max_line || !rate.price) {
          validationErrors[`rate_${index}`] = "All rate fields are required";
          isValid = false;
        }
      });

      // Ensure there is at least one plan rate when creating a new plan
      if (planData.plan_rates.length === 0) {
        validationErrors.plan_rates = "At least one price field must be added";
        isValid = false;
      }
    }

    setErrors(validationErrors);
    return isValid;
  };

  const validateRate = () => {
    let isValid = true;
    let validationErrors = {};
    setErrors({});

    Object.keys(rateData).forEach((field) => {
      if (!rateData[field]) {
        validationErrors[field] = "This field is required";
        isValid = false;
      }
    });

    if (
      rateData.min_line &&
      rateData.max_line &&
      parseInt(rateData.min_line) >= parseInt(rateData.max_line)
    ) {
      validationErrors.min_line = "Min line should be less than Max line";
      isValid = false;
    }

    setErrors((prev) => ({ ...prev, ...validationErrors }));
    return isValid;
  };

  const handleAddPlanRate = () => {
    if (validateRate()) {
      setPlanData((prev) => ({
        ...prev,
        plan_rates: [
          ...prev.plan_rates,
          {
            min_line: parseInt(rateData.min_line),
            max_line: parseInt(rateData.max_line),
            price: parseFloat(rateData.price).toFixed(2),
          },
        ],
      }));
      setRateData({ min_line: "", max_line: "", price: "" });
    }
  };

  const handleSubmitPlan = () => {
    setToast(false);

    if (validateForm()) {
      const filteredPlanRates = planData?.plan_rates?.filter(
        (rate) => rate.min_line || rate.max_line || rate.price
      );

      const updatedPlanData = { ...planData, plan_rates: filteredPlanRates };

      if (filteredPlanRates.length > 0 && id) {
        dispatch(editPlanAsync({ id, updatedData: updatedPlanData })).then(
          (res) => {
            setToast({ header: res?.payload?.message });
            dispatch(fetchPlanDetailsAsync(id));
          }
        );
      } else if (filteredPlanRates.length > 0) {
        dispatch(addPlanAsync(planData)).then((res) => {
          setToast({ header: res?.payload?.message });
        });
      }
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    // Reset form data and errors after successful submission
    if (isPlanAdded) {
      setPlanData({
        name: "",
        description: "",
        sku: "",
        activation_charge: "",
        device_type: "",
        plan_rates: [],
      });

      setRateData({
        min_line: "",
        max_line: "",
        price: "",
      });
    }
  }, [isPlanAdded]);

  const planDetailsFields = [
    {
      label: "Plan Name",
      name: "name",
      placeholder: "Enter plan name",
      type: "text",
      maxLength: 50,
    },
    {
      label: "SKU",
      name: "sku",
      placeholder: "Enter SKU number",
      type: "text",
      maxLength: 25,
    },
    {
      label: "Activation charge",
      name: "activation_charge",
      placeholder: "Enter activation charge",
      type: "number",
    },
  ];

  const addPlanFields = [
    {
      label: "Min line",
      name: "min_line",
      placeholder: "Min line value",
      type: "number",
    },
    {
      label: "Max line",
      name: "max_line",
      placeholder: "Max line value",
      type: "number",
    },
    { label: "Price", name: "price", placeholder: "Price", type: "text" },
  ];

  return (
    <div className="create-plan-container">
      <div className="previous-page" onClick={handleBack}>
        <BackBtn className="back-btn" />
        <p>Back</p>
      </div>
      <h1 className="title">{id ? "Edit Plan" : "Create Plan"}</h1>

      {/* Plan Details Form */}
      <div className="form-grid plan-details">
        {planDetailsFields.map((field, index) => (
          <div key={index} className="field-group">
            <label className="input-label">{field.label}</label>
            <input
              type={field.type}
              name={field.name}
              value={planData[field.name]}
              onChange={handleInputChange}
              placeholder={field.placeholder}
              className="input"
              maxLength={field?.maxLength}
            />
            {errors[field.name] && (
              <FormHelperText className="error-text" error>
                {errors[field.name]}
              </FormHelperText>
            )}
          </div>
        ))}

        <FormControl fullWidth className="custom-select-container">
          <label id="device-type-label">Select Device</label>
          <Select
            labelId="device-type-label"
            name="device_type"
            value={planData.device_type}
            onChange={handleInputChange}
            className="input custom-select"
            IconComponent={() => <DropdownIcon />}
            displayEmpty
            renderValue={(selected) =>
              selected || <span className="placeholder">Select Device</span>
            }
          >
            <MenuItem value="DEVICE">DEVICE</MenuItem>
            <MenuItem value="TABLET">TABLET</MenuItem>
          </Select>
          {errors.device_type && (
            <FormHelperText className="error-text" error>
              {errors.device_type}
            </FormHelperText>
          )}
        </FormControl>

        <FormControl
          fullWidth
          className="textarea-container"
          error={Boolean(errors.description)}
        >
          <label className="input-label">Description</label>
          <textarea
            name="description"
            maxLength={250}
            placeholder="Enter description here"
            rows={5}
            className="create-textarea"
            value={planData.description}
            onChange={handleInputChange}
          />
          {errors.description && (
            <FormHelperText sx={{ marginLeft: "0" }}>
              {errors.description}
            </FormHelperText>
          )}
        </FormControl>
      </div>

      {/* Add Plans Form */}
      <div className="form-grid add-plan">
        {addPlanFields.map((field, index) => (
          <div key={index} className="field-group">
            <label className="input-label">{field.label}</label>
            <input
              type={field.type}
              name={field.name}
              value={rateData[field.name]}
              onChange={handleRateChange}
              placeholder={field.placeholder}
              className="input"
            />
            {errors[field.name] && (
              <FormHelperText className="error-text" error>
                {errors[field.name]}
              </FormHelperText>
            )}
          </div>
        ))}
        <button onClick={handleAddPlanRate} className="add-plan-button">
          Add Price <PlusIcon />
        </button>
      </div>

      {/* Plans List */}
      {planData.plan_rates.length > 0 && (
        <div className="plans-list">
          {planData.plan_rates.map((plan, index) => (
            <div key={index} className="plan-item plan-added-item">
              <div className="field-group plan-added-group">
                <label className="input-label">Min line</label>
                <input
                  className="plan-value"
                  value={plan.min_line}
                  onChange={(e) =>
                    handleEditRateChange(index, "min_line", e.target.value)
                  }
                  readOnly={!plan.id} // Disable if there's no ID
                />
              </div>
              <div className="field-group plan-added-group">
                <label className="input-label">Max line</label>
                <input
                  className="plan-value"
                  value={plan.max_line}
                  onChange={(e) =>
                    handleEditRateChange(index, "max_line", e.target.value)
                  }
                  readOnly={!plan.id} // Disable if there's no ID
                />
              </div>
              <div className="field-group plan-added-group">
                <label className="input-label">Price</label>
                <input
                  className="plan-value"
                  value={plan.price}
                  onChange={(e) =>
                    handleEditRateChange(index, "price", e.target.value)
                  }
                  readOnly={!plan.id}
                />
              </div>
              <IconButton
                onClick={() => handleDeleteRate(index)}
                className="delete-plan-button"
              >
                <DeleteIcon />
              </IconButton>
            </div>
          ))}
        </div>
      )}
      {toast && !isError && <Toast toast={toast} />}
      <button onClick={handleSubmitPlan} className="add-plan-button">
        Submit
      </button>
      {isError && <p className="error">{adminTexts.error_message} </p>}
      {errors?.plan_rates && <div className="error">{errors?.plan_rates}</div>}
    </div>
  );
};

export default CreatePlan;
